import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useActiveStore } from "@/hooks/use-active-store";
import { useBusinessSettings, useSaveBusinessSettings } from "@/hooks/use-fulfillment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

export function BusinessSettingsSection() {
  const { storeId } = useActiveStore();
  const { data: settings, isLoading } = useBusinessSettings(storeId);
  const save = useSaveBusinessSettings();

  const form = useForm({
    defaultValues: {
      companyName: "",
      address: "",
      city: "",
      postalCode: "",
      country: "BA",
      phone: "",
      email: "",
    },
    onSubmit: async ({ value }) => {
      try {
        await save.mutateAsync({
          storeId,
          ...value,
        });
        toast.success("Poslovne postavke sačuvane");
      } catch (e: any) {
        toast.error(e.message || "Greška pri čuvanju");
      }
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        companyName: settings.companyName || "",
        address: settings.address || "",
        city: settings.city || "",
        postalCode: settings.postalCode || "",
        country: settings.country || "BA",
        phone: settings.phone || "",
        email: settings.email || "",
      });
    }
  }, [settings]);

  if (!storeId) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">
        Odaberite trgovinu za poslovne postavke
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">Podaci pošiljaoca za naljepnice</p>

      {isLoading ? (
        <div className="flex flex-col gap-2 max-w-lg">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10">
          <div className="border-b bg-muted/50 px-4 py-2">
            <span className="text-xs font-medium">Podaci o firmi</span>
          </div>
          <div className="p-4">
            <form onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }} className="flex flex-col gap-3 max-w-lg">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bs-company" className="text-xs">Naziv firme</Label>
                <form.Field name="companyName">
                  {(field) => <Input id="bs-company" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                </form.Field>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bs-address" className="text-xs">Adresa</Label>
                <form.Field name="address">
                  {(field) => <Input id="bs-address" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                </form.Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bs-postal" className="text-xs">Poštanski broj</Label>
                  <form.Field name="postalCode">
                    {(field) => <Input id="bs-postal" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                  </form.Field>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bs-city" className="text-xs">Grad</Label>
                  <form.Field name="city">
                    {(field) => <Input id="bs-city" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                  </form.Field>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bs-country" className="text-xs">Država</Label>
                  <form.Field name="country">
                    {(field) => <Input id="bs-country" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                  </form.Field>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bs-phone" className="text-xs">Telefon</Label>
                  <form.Field name="phone">
                    {(field) => <Input id="bs-phone" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} required />}
                  </form.Field>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bs-email" className="text-xs">Email (opciono)</Label>
                <form.Field name="email">
                  {(field) => <Input id="bs-email" type="email" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />}
                </form.Field>
              </div>
              <Button type="submit" className="self-start" disabled={save.isPending}>
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sačuvaj
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
